/**
 * SEARCH-002 — Hook moteur de la palette de recherche globale (⌘K).
 *
 * Extrait mécaniquement de `SearchPalette.tsx` (aucun changement de logique) :
 * tout l'état/dérivés/handlers (une instance par shell, gate `active`). Lecture +
 * navigation pures, `shouldFilter={false}`, gating RBAC strict, passerelle
 * Aide/Assistant via `useHelpBridge`. La couche « données » (requête, hits,
 * groupes, aperçu) vit dans `useSearchPaletteData` (sous-hook appelé ici).
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDebounce } from '@/components/shared/_referential-picker/useDebounce'
import { useEntity } from '@/contexts/EntityContext'
import { useHelpBridge } from '@/contexts/HelpContext'
import { useGlobalSearch } from '@/hooks/useGlobalSearch'
import { useSearchPreview, type PreviewTarget } from '@/hooks/useSearchPreview'
import { type SourceType } from '@/lib/searchCategories'
import { isLikelyQuestion } from '@/lib/searchIntent'
import { type QuickLookTarget, readRecentQueries, recordQuery } from './searchPaletteHelpers'
import { saisieCherchable } from './rechercheGlobale'
import { useSearchPaletteData } from './useSearchPaletteData'
import { useSearchPaletteActions } from './useSearchPaletteActions'
import { useSearchPaletteHandlers } from './useSearchPaletteHandlers'

// ===========================================================================
// HOOK — tout l'état/dérivés/handlers (une instance par shell, gate `active`).
// ===========================================================================
export interface UseSearchPaletteArgs {
    enableAssistant: boolean
    userId: string
    /** True si CE shell est celui du breakpoint courant (gate la requête réseau). */
    active: boolean
}

export function useSearchPalette({ enableAssistant, userId, active }: UseSearchPaletteArgs) {
    const navigate = useNavigate()
    const { isOpen, close } = useGlobalSearch()
    const { currentEntity, hasPermission } = useEntity()
    const { openWithHelpSearch, openHelp } = useHelpBridge()

    const entityId = currentEntity?.entityId ?? null

    const [input, setInput] = useState('')
    const [selectedValue, setSelectedValue] = useState('')
    // Sélection des actions « aucun résultat » — état DÉDIÉ, découplé du `value` de
    // cmdk : cet état n'a pas de CommandItem à surligner (les propositions sont des
    // boutons), et cmdk réinitialise `value` quand sa liste devient vide. État propre
    // ⇒ bague stable + Entrée fiable, sans course de timing avec cmdk.
    const [noResultSel, setNoResultSel] = useState('')
    const [mobilePreview, setMobilePreview] = useState<PreviewTarget | null>(null)
    const [recents, setRecents] = useState<string[]>([])
    const [scopeType, setScopeType] = useState<SourceType | null>(null)
    const [quickLook, setQuickLook] = useState<QuickLookTarget | null>(null)
    // Cible du compositeur d'email (contact/apprenant/collègue) — rendu par les 2 shells
    // PAR-DESSUS la palette (comme `quickLook`). On porte directement l'EMAIL (envoi
    // « classique » par adresses, pas de résolution d'id) ; tracé dans l'historique général.
    const [composeEmail, setComposeEmail] = useState<{ email: string; name: string } | null>(null)
    // Champ piloté par les 2 shells (centralisé ici pour pouvoir le RE-FOCUSER
    // après un clic sur un filtre — curseur prêt à taper sans bouger la souris).
    const inputRef = useRef<HTMLInputElement>(null)

    const debouncedQ = useDebounce(input.trim(), 250)

    // Couche « données » (requête réseau + dérivés) — sous-hook appelé
    // INCONDITIONNELLEMENT ici : l'ordre des hooks reste stable (identique à l'origine).
    const {
        scopableTypes,
        enabled,
        data,
        isLoading,
        isError,
        isTimeout,
        typosSkipped,
        refetch,
        hits,
        bestHit,
        groups,
        previewTarget,
        desktopPreviewQuery,
    } = useSearchPaletteData({
        active,
        isOpen,
        debouncedQ,
        entityId,
        scopeType,
        selectedValue,
        hasPermission,
    })

    // Reset à l'OUVERTURE **et** à la FERMETURE : le champ (header desktop) ne
    // conserve jamais l'ancienne saisie une fois replié, et repart propre.
    useEffect(() => {
        if (!active) return
        setInput('')
        setSelectedValue('')
        setNoResultSel('')
        setMobilePreview(null)
        setScopeType(null)
        setQuickLook(null)
        // NB : on ne réinitialise PAS `composeEmail` ici — `openComposeEmail` ferme la
        // palette JUSTE AVANT d'ouvrir le compositeur ; le vider ici l'annulerait.
        // Il est remis à zéro par la fermeture du Dialog (`onOpenChange(false)`).
        if (isOpen) setRecents(readRecentQueries(userId))
    }, [active, isOpen, userId])

    const mobilePreviewQuery = useSearchPreview(mobilePreview, entityId)

    const rememberQuery = useCallback(() => {
        recordQuery(userId, input)
    }, [userId, input])

    // Recherches récentes : mémoriser le terme dès qu'une recherche ABOUTIT à des
    // résultats, MÊME sans sélectionner de résultat (une simple recherche suffit —
    // parité avec le drawer d'aide). Sans ça, seul `rememberQuery` (ouverture d'un
    // hit / création / aide) alimentait l'historique. Filtré aux recherches non
    // vides qui donnent au moins un résultat (évite d'enregistrer les fautes de
    // frappe sans résultat). `readRecentQueries` au prochain affichage suffit —
    // pas besoin de rafraîchir `recents` en direct (la liste ne s'affiche qu'à
    // champ vide).
    useEffect(() => {
        if (!isOpen) return
        if (!saisieCherchable(debouncedQ)) return
        if (isLoading || isError || hits.length === 0) return
        recordQuery(userId, debouncedQ)
    }, [isOpen, debouncedQ, isLoading, isError, hits.length, userId])

    // Handlers navigation / aide / email / scope (sous-hook appelé
    // INCONDITIONNELLEMENT à sa position d'origine — bloc de `useCallback` contigu).
    const {
        go,
        openHit,
        handleHelp,
        handleBrowseHelp,
        clearRecents,
        removeRecent,
        openQuickLook,
        canSendEmail,
        openComposeEmail,
        pickScope,
    } = useSearchPaletteHandlers({
        close,
        navigate,
        inputRef,
        openWithHelpSearch,
        openHelp,
        input,
        userId,
        rememberQuery,
        setRecents,
        setQuickLook,
        setComposeEmail,
        setScopeType,
    })

    // `isLikelyQuestion` ne sert plus qu'à (1) juger la pertinence de « Créer » et (2)
    // la présélection — plus d'orientation aide vs assistant (aide unifiée §4).
    const isQuestion = isLikelyQuestion(input)
    const hasQuery = input.trim().length > 0

    const showEmptyState = debouncedQ.length === 0
    // Sous 3 caractères (sauf un identifiant), le serveur ne cherche rien : la palette le dit.
    const showTooShort = debouncedQ.length > 0 && !saisieCherchable(debouncedQ)
    const showNoResults = enabled && !isLoading && !isError && hits.length === 0
    const showResults = enabled && hits.length > 0
    // Aperçu/meilleur résultat UNIQUEMENT sur des résultats de la requête COURANTE.
    // Sinon, à champ vidé, `keepPreviousData` laisserait des « fantômes » (aperçu +
    // meilleur résultat de la recherche précédente) par-dessus l'état vide.
    const hasPreviewColumn = showResults

    // Création contextuelle + actions « aucun résultat » + handlers clavier
    // (sous-hook appelé INCONDITIONNELLEMENT à sa position d'origine).
    const {
        createOptions,
        primaryCreate,
        launchPrimaryCreate,
        noResultActions,
        handleCommandKeyDown,
        handleInputKeyDown,
    } = useSearchPaletteActions({
        hits,
        bestHit,
        input,
        hasQuery,
        isQuestion,
        scopeType,
        showNoResults,
        selectedValue,
        noResultSel,
        setSelectedValue,
        setNoResultSel,
        setScopeType,
        hasPermission,
        go,
        rememberQuery,
        handleHelp,
    })

    return {
        // store
        isOpen,
        close,
        // state
        input,
        setInput,
        scopeType,
        setScopeType,
        selectedValue,
        setSelectedValue,
        mobilePreview,
        setMobilePreview,
        recents,
        clearRecents,
        removeRecent,
        quickLook,
        setQuickLook,
        openQuickLook,
        composeEmail,
        setComposeEmail,
        openComposeEmail,
        canSendEmail,
        inputRef,
        pickScope,
        // context
        entityId,
        hasPermission,
        scopableTypes,
        enableAssistant,
        // data
        data,
        hits,
        groups,
        bestHit,
        isLoading,
        isError,
        isTimeout,
        typosSkipped,
        refetch,
        enabled,
        previewTarget,
        desktopPreviewQuery,
        mobilePreviewQuery,
        // flags
        showEmptyState,
        showTooShort,
        showNoResults,
        showResults,
        hasPreviewColumn,
        hasQuery,
        isQuestion,
        createOptions,
        primaryCreate,
        noResultActions,
        noResultSel,
        setNoResultSel,
        // handlers
        go,
        launchPrimaryCreate,
        openHit,
        handleHelp,
        handleBrowseHelp,
        rememberQuery,
        handleCommandKeyDown,
        handleInputKeyDown,
    }
}

export type SearchPaletteApi = ReturnType<typeof useSearchPalette>
